<!--
  Copyright (C) 2022 - Jeci SARL - https://jeci.fr

  This program is free software: you can redistribute it and/or modify
  it under the terms of the GNU Affero General Public License as
  published by the Free Software Foundation, either version 3 of the
  License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
-->
<template>
  <div class="basket-container">
    <div class="basket-header">
      <div class="basket-header-left">
        <i class="ri-inbox-line"></i>
        <span class="basket-title">{{ $t("dashboardAdmin.basket") }}</span>
        <span v-if="basketBlocks.length > 0" class="basket-count">{{
          basketBlocks.length
        }}</span>
      </div>
      <PrimeButton
        icon="ri-close-line"
        text
        rounded
        size="small"
        @click="$emit('close')"
        severity="secondary"
      />
    </div>
    <div class="basket-content">
      <div v-if="basketBlocks.length === 0" class="basket-empty">
        <i class="ri-inbox-line basket-empty-icon"></i>
        <p class="basket-empty-text">{{ $t("dashboardAdmin.basketEmpty") }}</p>
      </div>
      <div
        v-for="block in basketBlocks"
        :key="'basket-' + block.id"
        class="basket-block"
        :class="{ 'basket-block-selected': selectedBlockId === block.id }"
        :draggable="true"
        @dragstart="$emit('basket-dragstart', $event, block)"
        @dragend="$emit('basket-dragend', $event)"
        @click="$emit('select-block', block)"
      >
        <div class="basket-block-icon">
          <i class="ri-layout-grid-line"></i>
        </div>
        <div class="basket-block-info">
          <div class="basket-block-title">
            {{ block.title || block.type }}
          </div>
          <div class="basket-block-type">{{ block.type }}</div>
        </div>
        <div class="basket-block-actions">
          <PrimeButton
            icon="ri-pencil-line"
            text
            rounded
            size="small"
            severity="secondary"
            @click.stop="$emit('edit-block', block)"
          />
          <PrimeButton
            icon="ri-delete-bin-line"
            text
            rounded
            size="small"
            severity="danger"
            @click.stop="$emit('delete-block', block)"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Button from "primevue/button";

export default {
  name: "BasketPanel",
  components: {
    PrimeButton: Button,
  },
  props: {
    basketBlocks: {
      type: Array,
      required: true,
      default: () => [],
    },
    selectedBlockId: {
      type: String,
      default: null,
    },
  },
  emits: [
    "basket-dragstart",
    "basket-dragend",
    "select-block",
    "edit-block",
    "delete-block",
    "close",
  ],
};
</script>

<style scoped>
.basket-container {
  position: fixed;
  bottom: 1rem;
  right: calc(420px + 250px + 1rem);
  width: 350px;
  max-height: 500px;
  background: var(--p-content-background);
  border: 2px solid var(--p-content-border-color);
  border-radius: 0.5rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 100;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.basket-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1rem;
  background: var(--p-content-background);
  border-bottom: 1px solid var(--p-content-border-color);
}

.basket-header-left {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.basket-title {
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--p-text-color);
}

.basket-count {
  background: var(--p-primary-500);
  color: white;
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.125rem 0.5rem;
  border-radius: 1rem;
  min-width: 1.5rem;
  text-align: center;
}

.basket-content {
  flex: 1;
  overflow-y: auto;
  padding: 0.75rem;
  background: var(--p-surface-ground);
}

.basket-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  text-align: center;
}

.basket-empty-icon {
  font-size: 2rem;
  color: var(--p-text-muted-color);
}

.basket-empty-text {
  color: var(--p-text-muted-color);
  margin-top: 0.5rem;
}

.basket-block {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  background: var(--p-content-background);
  border: 2px solid var(--p-content-border-color);
  border-radius: 0.5rem;
  margin-bottom: 0.5rem;
  cursor: move;
  transition: all 0.2s;
}

.basket-block:hover {
  border-color: var(--p-primary-300);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.basket-block.basket-block-selected {
  border-color: var(--p-primary-500);
  background: var(--p-primary-50);
  box-shadow: 0 0 0 2px var(--p-primary-100);
}

.basket-block.dragging {
  opacity: 0.5;
  transform: scale(0.95);
  filter: brightness(0.9);
  transition: all 0.2s ease;
}

.basket-block-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  background: var(--p-primary-100);
  color: var(--p-primary-600);
  border-radius: 0.375rem;
  font-size: 1.25rem;
  flex-shrink: 0;
}

.basket-block-info {
  flex: 1;
  min-width: 0;
}

.basket-block-title {
  font-weight: 600;
  color: var(--p-text-color);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: 0.125rem;
}

.basket-block-type {
  font-size: 0.75rem;
  color: var(--p-text-muted-color);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.basket-block-actions {
  display: flex;
  gap: 0.25rem;
  flex-shrink: 0;
}
</style>
